/**
 * Cycle 8 STORY-703: Placeholder testimonials data.
 *
 * **Note:** These are illustrative testimonials for visual demonstration.
 * Replace with real customer feedback after pilot engagements close.
 *
 * Each testimonial supports filtering by suite/product slug for context-aware
 * display on Suite Landing pages and Product Detail pages.
 */

export interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role: string;
  company: string;
  industry: string;
  /** Optional filter — when set, testimonial shown only on these contexts */
  showOnSuite?: string[];
  showOnProduct?: string[];
  /** Initials for avatar fallback (no real photos in placeholder data) */
  initials: string;
  /** Visual accent color for the avatar */
  accentColor: string;
}

export const testimonials: Testimonial[] = [
  {
    id: "t-001",
    quote: "Внедрили shift-brief за 3 недели. Гейм-механики реально работают: вовлечённость в обучение по охране труда выросла в 2.5 раза. XP-баллы и бейджи мотивируют гораздо сильнее обычных тестов.",
    author: "Анна Сергеева",
    role: "Директор по охране труда",
    company: "ГМК «Полюс Восток»",
    industry: "горнодобыча",
    showOnSuite: ["safety"],
    showOnProduct: ["shift-brief-safety"],
    initials: "АС",
    accentColor: "#2c5f2d",
  },
  {
    id: "t-002",
    quote: "core-logic дал нам видимость по гранулометрии в реальном времени — раньше операторы ждали лабораторные результаты часами. Качество концентрата стабильнее, потерь руды меньше.",
    author: "Дмитрий Воронов",
    role: "Главный инженер обогатительной фабрики",
    company: "ПАО «Северсталь Групп»",
    industry: "горнодобыча",
    showOnProduct: ["core-logic"],
    initials: "ДВ",
    accentColor: "#1a4f7a",
  },
  {
    id: "t-003",
    quote: "Раньше планировщик паллет работал 4-6 часов на смену. С smart-pallet и palletix — меньше 30 минут. Логисты сначала не верили, что алгоритм считает оптимально.",
    author: "Елена Кравцова",
    role: "Руководитель отдела логистики",
    company: "X5 Retail Group",
    industry: "retail",
    showOnSuite: ["logistics"],
    showOnProduct: ["smart-pallet", "palletix", "pallet-planner"],
    initials: "ЕК",
    accentColor: "#7a4f1a",
  },
  {
    id: "t-004",
    quote: "Прогнозы продаж по регионам теперь учитывают сезонность и промо. ROI калькулятор показал срок окупаемости 4 месяца — фактически окупились за 2.5.",
    author: "Игорь Михайлов",
    role: "Коммерческий директор",
    company: "ТД «АгроПром»",
    industry: "FMCG",
    showOnSuite: ["sales-intelligence"],
    showOnProduct: ["sales-planner", "sales-analysis", "sales-platinum"],
    initials: "ИМ",
    accentColor: "#5f2c5f",
  },
  {
    id: "t-005",
    quote: "Crew-processing помог нам перейти от ручного управления агентами к декларативному. Настроили один раз — работает автономно. На пиковых нагрузках сам распределяет нагрузку.",
    author: "Алексей Гончаров",
    role: "Архитектор инфраструктуры",
    company: "Газпромнефть-ИТО",
    industry: "energy",
    showOnProduct: ["crew-processing"],
    initials: "АГ",
    accentColor: "#1a5f5f",
  },
  {
    id: "t-006",
    quote: "Платформа IT-Navigate закрыла нам 4 разрозненных задачи единым подходом: безопасность, логистика, прогнозы. Раньше тендерили 4 разных вендоров — сейчас один партнёр.",
    author: "Виктория Орлова",
    role: "ИТ-директор",
    company: "Холдинг «Ростех-Машиностроение»",
    industry: "производство",
    initials: "ВО",
    accentColor: "#5f1a1a",
  },
  {
    id: "t-007",
    quote: "instruct-me с FaceID-верификацией закрыл нам аудиторскую дыру: теперь у нас есть юридически валидное доказательство, что инструктаж прошёл именно тот сотрудник.",
    author: "Сергей Беляев",
    role: "Руководитель службы охраны труда",
    company: "Трубная Металлургическая Компания",
    industry: "металлургия",
    showOnSuite: ["safety"],
    showOnProduct: ["instruct-me"],
    initials: "СБ",
    accentColor: "#2c5f3d",
  },
];

/**
 * Filter testimonials by suite name (case-insensitive).
 * Returns testimonials shown on this suite OR no filter set (general).
 */
export function getTestimonialsForSuite(suite: string): Testimonial[] {
  const normalized = suite.toLowerCase();
  return testimonials.filter(
    (t) =>
      !t.showOnSuite ||
      t.showOnSuite.length === 0 ||
      t.showOnSuite.some((s) => s.toLowerCase() === normalized)
  );
}

/**
 * Filter testimonials by product slug.
 * Returns testimonials specifically tied to this product.
 */
export function getTestimonialsForProduct(slug: string): Testimonial[] {
  return testimonials.filter(
    (t) => t.showOnProduct && t.showOnProduct.includes(slug)
  );
}

/**
 * Get general testimonials (no specific filter; suitable for HomePage).
 */
export function getGeneralTestimonials(limit = 5): Testimonial[] {
  return testimonials.slice(0, limit);
}
